// Per-dialect rename emitter contract — Slice 27 ships the contract + stubs;
// Slices 29–33 fill in the dialect-native ALTER syntax.
//
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 27 (this file's contract) +
// § Slice 29 mssql / 30 postgres / 31 mysql / 32 oracle / 33 sqlite.
//
// The planner (renamePlanner.ts) calls `emitObjectBodyAlter` for every object
// whose body has been rewritten in-place via `emitSource`, and gets back the
// dialect-native `ALTER VIEW` / `ALTER PROCEDURE` / etc. wrapper SQL. All
// per-dialect emitters have now landed (Slices 29–33 complete); the
// placeholder/stub shape defined here is retained as a defensive fallback —
// the executor still refuses any placeholder that somehow reaches apply.

import type { ObjectKind, QualifiedName } from '../../types';

export type EmitObjectBodyAlterInput = {
  readonly objectKind: ObjectKind;
  readonly objectName: QualifiedName;
  readonly newBody: string;
};

/** Schema-side rename for a table or view — Slice 36. The per-dialect
 *  emitters each handle this differently (mssql: `sp_rename`, pgsql:
 *  `ALTER TABLE/VIEW … RENAME TO`, oracle/sqlite: non-destructive
 *  `RENAME`/`ALTER TABLE … RENAME TO` which works for views too, mysql: no
 *  `RENAME VIEW` form so the original view body is recreated under the new
 *  name then the old view dropped — see `viewBody`). */
export type SchemaRenameInput = {
  readonly kind: 'table' | 'view';
  readonly from: QualifiedName;
  readonly to: QualifiedName;
  /** NAV-2 — the renamed view's ORIGINAL `CREATE`/`SELECT` body, supplied by
   *  the planner for `kind: 'view'`. Engines without a native view-rename
   *  (MySQL) recreate the view with this real definition instead of the
   *  data-destroying `SELECT * FROM <old>` placeholder. Left `undefined` for
   *  table renames and for view-rename dialects that do a real RENAME
   *  (Oracle/SQLite — they don't need the body). When a MySQL view rename has
   *  no body available the emitter MUST refuse, never emit the placeholder. */
  readonly viewBody?: string;
};

export type DialectRenameEmitter = {
  /** Rewrap the rewritten body in the dialect-native ALTER statement. The
   *  Slice-27 stub shape is a historical contract artifact retained as a
   *  defensive fallback; all per-dialect emitters (Slices 29–33) have landed
   *  and the executor refuses any unfilled placeholder that reaches apply. */
  readonly emitObjectBodyAlter: (input: EmitObjectBodyAlterInput) => string;
  /** Slice 36 — emit the schema-side ALTER for a table or view rename. */
  readonly emitTableOrViewSchemaAlter: (input: SchemaRenameInput) => string;
};

const STUB_PREFIX = '-- TODO(rename emit):';

/** Infix that distinguishes a NAV-2 typed unrewritable-rename refusal (the
 *  emitter HAS landed but refused to avoid data loss) from a genuine
 *  not-yet-implemented Slice-27 stub. Both share `STUB_PREFIX` so the
 *  executor's pre-flight refuses either, but only this one carries a real,
 *  user-surfaceable reason. */
const UNREWRITABLE_INFIX = 'UNREWRITABLE — ';

/** True when `sql` was produced by a Slice-27 stub emitter (a not-yet-filled
 *  placeholder) — NOT a NAV-2 unrewritable-rename refusal. All per-dialect
 *  emitters (Slices 29–33) have now landed, so this path is defensive: the
 *  Slice-28 executor uses it to refuse any placeholder that somehow reaches
 *  apply via the generic internal-invariant diagnostic. */
export function isStubAlterSql(sql: string): boolean {
  const trimmed = sql.trimStart();
  return (
    trimmed.startsWith(STUB_PREFIX) && !trimmed.includes(UNREWRITABLE_INFIX)
  );
}

/** True when `sql` is a NAV-2 typed unrewritable-rename refusal (produced by
 *  `unrewritableRenameSql`). Distinct from `isStubAlterSql` so the executor
 *  can surface the embedded reason instead of the generic defensive-stub
 *  diagnostic. */
export function isUnrewritableRenameSql(sql: string): boolean {
  const trimmed = sql.trimStart();
  return (
    trimmed.startsWith(STUB_PREFIX) && trimmed.includes(UNREWRITABLE_INFIX)
  );
}

/** Extracts the human-readable refusal reason embedded by
 *  `unrewritableRenameSql`. Returns `null` when `sql` is not an unrewritable
 *  marker. */
export function unrewritableRenameReason(sql: string): string | null {
  const trimmed = sql.trimStart();
  const at = trimmed.indexOf(UNREWRITABLE_INFIX);
  if (!trimmed.startsWith(STUB_PREFIX) || at < 0) return null;
  return trimmed.slice(at + UNREWRITABLE_INFIX.length).trim();
}

/** Helper used by every per-dialect stub. Centralises the placeholder shape
 *  so the matcher above stays trivial. */
export function stubAlterSql(
  dialect: string,
  input: EmitObjectBodyAlterInput,
  sliceRef: string,
): string {
  const qn = input.objectName;
  const fq = [qn.database, qn.schema, qn.name].filter(Boolean).join('.');
  return [
    `${STUB_PREFIX} dialect=${dialect} kind=${input.objectKind} name=${fq} (filled by Slice ${sliceRef})`,
    input.newBody,
  ].join('\n');
}

/** NAV-2 — a typed refusal for a rename the dialect genuinely cannot perform
 *  without losing data (MySQL view rename with no original body available).
 *  Reuses `STUB_PREFIX` (so the Slice-28 executor still refuses the whole
 *  batch and never runs a data-destroying `CREATE VIEW … SELECT * FROM old;
 *  DROP VIEW old;` placeholder) but adds the `UNREWRITABLE_INFIX` so the
 *  executor distinguishes this from a genuine not-yet-implemented stub and
 *  surfaces `reason` to the user. NEVER return the placeholder — refuse
 *  instead. */
export function unrewritableRenameSql(dialect: string, reason: string): string {
  return `${STUB_PREFIX} dialect=${dialect} ${UNREWRITABLE_INFIX}${reason}`;
}
